import {useSlider} from "../hooks/useSlider";
import {ChevronArrowLeftIcon, ChevronArrowRightIcon} from "./common/Icons";
import Picture from "./common/Picture";
import {Slide} from "../types";
import {memo} from "react";

interface SideTitleSliderSectionProps {
    id: string;
    title: string;
    description?: string;
    isDark?: boolean;
    isLeftAligned?: boolean;
    slides: Slide[];
}

export const SideTitleSliderSection = memo(({
                                                id,
                                                title,
                                                description,
                                                isDark = false,
                                                isLeftAligned = true,
                                                slides
                                            }: SideTitleSliderSectionProps) => {
    const {activeIndex, progress, goToNext, goToPrev, goToSlide} = useSlider(slides.length);

    return (
        <section
            id={id}
            className={`snap-section ${isDark ? 'bg-dark-mode' : 'bg-ivory'} py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8`}>
            <div
                className={`flex flex-col ${isLeftAligned ? 'lg:flex-row' : 'lg:flex-row-reverse'} h-full gap-4 sm:gap-6 lg:gap-8`}>
                {/* Title Section */}
                <div
                    className={`flex flex-col justify-center gap-1 sm:gap-2 lg:gap-4 lg:w-[34%] text-center lg:text-start ${
                        !isLeftAligned ? 'lg:text-end' : ''}`}
                >
                    <h2
                        className={`heading-font text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight 
                        ${isDark ? 'text-ivory' : 'text-royal-pine'}`}
                    >
                        {title}
                    </h2>
                    {description && (
                        <p className={`text-sm sm:text-base md:text-lg px-4 lg:px-0 ${isDark ? 'text-ivory' : 'text-royal-pine'}`}>
                            {description}
                        </p>
                    )}
                </div>

                {/* Slider Content */}
                <div
                    className="w-full flex-1 flex flex-col min-h-0">
                    <div
                        className={`flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 mb-4 lg:mb-2 flex-1 min-h-0
                        ${!isLeftAligned ? 'lg:flex-row-reverse' : ''}`}
                    >
                        {/* Main Image */}
                        <div
                            className="relative h-64 sm:h-80 md:h-full rounded-lg overflow-hidden shadow-lg lg:w-3/4">
                            <Picture
                                {...(slides[activeIndex].image.toLowerCase().endsWith('.webp') ? { webpSrc: slides[activeIndex].image } : slides[activeIndex].image.toLowerCase().endsWith('.png') ? { pngSrc: slides[activeIndex].image } : { jpgSrc: slides[activeIndex].image })}
                                alt={slides[activeIndex].title || ""}
                                className="w-full h-full object-cover transition-opacity duration-500"
                                key={activeIndex}
                            />

                            {/* Title Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 text-ivory">
                                {slides[activeIndex].title && (
                                    <div className="flex p-3 sm:p-4 items-center bg-royal-pine">
                                        <p className="text-sm sm:text-base tracking-wider font-medium">
                                            {slides[activeIndex].title}
                                        </p>
                                    </div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white bg-opacity-30">
                                    <div
                                        className="h-1 bg-white"
                                        style={{width: `${progress}%`, transition: 'width 0.05s linear'}}/>
                                </div>
                            </div>
                        </div>

                        {/* Thumbnails */}
                        <div
                            className="hidden lg:flex lg:flex-col justify-start lg:justify-between space-y-3 lg:space-y-4 lg:space-x-0 overflow-x-auto lg:overflow-x-visible scrollbar-hide pb-2 lg:pb-0 min-w-0 lg:w-1/4">
                            {slides.map((slide, index) => (
                                <div
                                    key={index}
                                    className="relative bg-white rounded-lg cursor-pointer shadow-md overflow-hidden flex-shrink-0 lg:flex-1 w-20 sm:w-24 md:w-28 lg:w-full h-20 sm:h-24 md:h-28 lg:h-auto transition-all duration-300"
                                    onClick={() => goToSlide(index)}
                                >
                                    <Picture
                                        {...(slide.image.toLowerCase().endsWith('.webp') ? { webpSrc: slide.image } : slide.image.toLowerCase().endsWith('.png') ? { pngSrc: slide.image } : { jpgSrc: slide.image })}
                                        alt={slide.title || ""}
                                        className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex justify-center items-center space-x-4 sm:space-x-6 lg:space-x-8 w-full">
                        {/* Previous Button*/}
                        <button
                            onClick={goToPrev}
                            className={`p-2 sm:p-3 rounded-full transition-colors focus:outline-none focus:ring-2
                            ${isDark ? 'text-ivory hover:bg-ivory/25 focus:ring-ivory/50' : 'text-royal-pine hover:bg-royal-pine/25 focus:ring-royal-pine/50'}`}
                            aria-label="Previous slide"
                        >
                            <ChevronArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5"/>
                        </button>

                        {/* Dot Indicators */}
                        <div
                            className="flex flex-1 justify-center items-center space-x-2">
                            {slides.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`h-1 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1
                                    ${activeIndex === index
                                        ? `${isDark ? 'bg-ivory focus:ring-ivory/50' : 'bg-royal-pine focus:ring-royal-pine/50'} w-8 sm:w-8`
                                        : `${isDark ? 'bg-ivory/25 hover:bg-ivory/50' : 'bg-royal-pine/25 hover:bg-royal-pine/50'} w-4 sm:w-4`
                                    }`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>

                        {/* Next Button*/}
                        <button
                            onClick={goToNext}
                            className={`p-2 sm:p-3 rounded-full transition-colors focus:outline-none focus:ring-2
                            ${isDark ? 'text-ivory hover:bg-ivory/25 focus:ring-ivory/50' : 'text-royal-pine hover:bg-royal-pine/25 focus:ring-royal-pine/50'}`}
                            aria-label="Next slide"
                        >
                            <ChevronArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5"/>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
});

SideTitleSliderSection.displayName = 'SideTitleSliderSection';